import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Trash2, RotateCcw, X, Search } from 'lucide-react';
import {
  useTrashedRevenueDistributions,
  useRestoreRevenueDistributions,
  usePermanentlyDeleteRevenueDistributions,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { money, doctorName, PAYOUT_PAGE_SIZE } from './payoutShared';
import { SourceBadge } from './payoutBadges';

type ConfirmAction = { kind: 'restore' | 'delete'; ids: string[] } | null;

/**
 * Trash — removed revenue distributions.
 *
 * Restore or permanently delete removed accruals. Settled records are protected
 * by the backend (they come back as `skipped`), so this view can never break the
 * payout ledger's history.
 */
export function PayoutTrash() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const headerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPage(1);
  }, [debounced]);

  const { data, isLoading, error } = useTrashedRevenueDistributions(
    page,
    PAYOUT_PAGE_SIZE,
    { search: debounced.trim() || undefined },
  );
  const trashed = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const restore = useRestoreRevenueDistributions();
  const permanentDelete = usePermanentlyDeleteRevenueDistributions();

  // Drop selected ids that scrolled off the current page.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(trashed.map((d: any) => d.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [trashed]);

  const allSelected =
    trashed.length > 0 && trashed.every((d: any) => selectedIds.includes(d.id));
  const someSelected = selectedIds.length > 0;
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : trashed.map((d: any) => d.id));
  const toggleOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const confirm = async () => {
    if (!confirmAction) return;
    try {
      if (confirmAction.kind === 'restore') {
        await restore.mutateAsync(confirmAction.ids);
        toast.success('Restored successfully');
      } else {
        const res: any = await permanentDelete.mutateAsync(confirmAction.ids);
        const skipped = res?.data?.skipped?.length ?? 0;
        toast.success(
          `Permanently deleted ${res?.data?.deleted?.length ?? 0}${skipped ? ` · ${skipped} settled item(s) skipped` : ''}`,
        );
      }
      setSelectedIds([]);
    } catch {
      toast.error(
        confirmAction.kind === 'restore' ? 'Failed to restore' : 'Failed to delete permanently',
      );
    } finally {
      setConfirmAction(null);
    }
  };

  const busy = restore.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search trashed distributions…"
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-base font-semibold text-text">Trashed Distributions</h2>
          <p className="text-xs text-text-muted">
            Restore or permanently delete removed revenue shares. Settled records cannot be
            deleted.
          </p>
        </div>

        {someSelected && (
          <div className="bg-primary/5 border-b border-primary/20 p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-text">
              <span className="font-medium">{selectedIds.length}</span> selected
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction({ kind: 'restore', ids: selectedIds })}
                disabled={busy}
                className="px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                {restore.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RotateCcw size={14} />
                )}
                Restore
              </button>
              <button
                type="button"
                onClick={() => setConfirmAction({ kind: 'delete', ids: selectedIds })}
                disabled={busy}
                className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                {permanentDelete.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete permanently
              </button>
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors flex items-center gap-1.5"
              >
                <X size={14} />
                Clear
              </button>
            </div>
          </div>
        )}

        {error ? (
          <div className="flex items-center gap-2 p-4 text-danger">
            <AlertCircle size={18} />
            <span>Failed to load trashed distributions.</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-background/50">
                    <th className="px-4 py-3 w-10 text-left">
                      <input
                        ref={headerRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={trashed.length === 0}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                        title="Select all"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Source / Item
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Payee
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Share
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                      Deleted
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
                      </td>
                    </tr>
                  ) : trashed.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-text-muted">
                        Trash is empty.
                      </td>
                    </tr>
                  ) : (
                    trashed.map((d: any) => {
                      const isConsultation = d.sourceType === 'CONSULTATION';
                      return (
                        <tr key={d.id} className="hover:bg-background/50 transition-colors">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(d.id)}
                              onChange={() => toggleOne(d.id)}
                              className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <SourceBadge source={d.sourceType} />
                            <p className="text-sm font-medium text-text mt-1">
                              {isConsultation
                                ? d.appointment?.appointmentType || 'Consultation'
                                : d.sourceType === 'ADMISSION'
                                  ? 'IPD admission'
                                  : d.receiptItem?.testName || 'N/A'}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-sm text-text">
                            {d.referral?.name || doctorName(d.doctor)}
                          </td>
                          <td className="px-4 py-3 text-sm text-primary text-right font-medium">
                            {money(d.doctorShare)}
                          </td>
                          <td className="px-4 py-3 text-sm text-text-muted">
                            {d.deletedAt ? new Date(d.deletedAt).toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setConfirmAction({ kind: 'restore', ids: [d.id] })}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors"
                                title="Restore"
                              >
                                <RotateCcw size={16} />
                              </button>
                              <button
                                onClick={() => setConfirmAction({ kind: 'delete', ids: [d.id] })}
                                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                                title="Delete permanently"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={meta?.totalPages || 1}
              total={meta?.total || 0}
              limit={PAYOUT_PAGE_SIZE}
              onPageChange={setPage}
              itemName="trashed items"
            />
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.kind === 'restore' ? 'Restore distributions?' : 'Delete permanently?'}
        description={
          confirmAction?.kind === 'restore'
            ? `${confirmAction.ids.length} distribution(s) will be restored to the ledger.`
            : `${confirmAction?.ids.length ?? 0} distribution(s) will be permanently deleted. Settled records are skipped.`
        }
        confirmLabel={confirmAction?.kind === 'restore' ? 'Restore' : 'Delete permanently'}
        variant={confirmAction?.kind === 'restore' ? 'primary' : 'danger'}
        loading={busy}
        onConfirm={confirm}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
